import React, { useState, useMemo } from 'react';
import { Table, Spin, Button, message, Modal } from 'antd';
import { Participant } from './TeacherOlympiadList';
import { getParticipantsTableColumns } from './ParticipantsTableColumns';
import { CheckCircleOutlined } from '@ant-design/icons';
import { postRequest } from '../../../../api/httpRequest';

interface ParticipantsTableProps {
    participants: Participant[];
    loading: boolean;
    passingScore: number;
    maxScore: number;
    olympiadId: number;
    isCurator?: boolean;
    onDataUpdate?: () => void;
}

const ParticipantsTable: React.FC<ParticipantsTableProps> = ({
    participants,
    loading,
    passingScore,
    maxScore,
    olympiadId,
    isCurator = false,
    onDataUpdate
}) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleResultUpdate = () => {
        if (onDataUpdate) {
            onDataUpdate();
        }
    };

    const columns = useMemo(() => getParticipantsTableColumns(
        participants,
        passingScore, 
        olympiadId,
        maxScore,
        handleResultUpdate, 
        isCurator
    ), [participants, passingScore, olympiadId, maxScore, handleResultUpdate, isCurator]);

    const isParticipantAdmitted = (participant: Participant): boolean => {
        const score = parseInt(participant.firstTourOption1 || '0');
        return score >= passingScore;
    };

    const handleAutoSelect = () => {
        const admittedParticipants = participants
            .filter(participant => isParticipantAdmitted(participant))
            .map(participant => participant.id);
        
        setSelectedRowKeys(admittedParticipants);
        message.info(`Автоматически выбрано ${admittedParticipants.length} участников`);
    };

    const handleAdmitParticipants = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Выберите участников для допуска');
            return;
        }

        Modal.confirm({
            title: 'Подтверждение допуска',
            content: `Вы уверены, что хотите допустить ${selectedRowKeys.length} участников ко второму туру?`,
            onOk: async () => {
                setIsSubmitting(true);
                try {
                    postRequest('/server/olympiad/teacher/firstTourPass', {
                        olympiadId: olympiadId,
                        participantIds: selectedRowKeys
                    })
                    .then(resp => {
                        message.success('Участники успешно допущены ко второму туру');
                        setSelectedRowKeys([]);
                        if (onDataUpdate) {
                            onDataUpdate();
                        }
                    })
                    .catch(error => {
                        throw new Error('Ошибка при отправке данных');
                    });
                } catch (error) {
                    console.error('Ошибка при отправке данных:', error);
                    message.error('Произошла ошибка при отправке данных');
                } finally {
                    setIsSubmitting(false);
                }
            }
        });
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys: React.Key[], selectedRows: any) => {
            setSelectedRowKeys(selectedKeys);
        },
        getCheckboxProps: (record: Participant) => ({
            disabled: !isParticipantAdmitted(record),
        }),
    };

    return (
        <Spin spinning={loading || isSubmitting}>
            {!isCurator && (
                <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={handleAdmitParticipants}
                        disabled={selectedRowKeys.length === 0}
                        loading={isSubmitting}
                    >
                        Допустить выбранных ({selectedRowKeys.length})
                    </Button>
                    
                    <Button
                        onClick={handleAutoSelect}
                        disabled={participants.length === 0}
                    >
                        Автовыбор по баллам
                    </Button>
                </div>
            )}

            <Table
                columns={columns}
                dataSource={participants}
                rowKey="id"
                rowSelection={!isCurator ? {
                    type: 'checkbox',
                    ...rowSelection,
                } : undefined}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} из ${total} участников`
                }}
                scroll={{ x: 2500 }}
                size="small"
            />
        </Spin>
    );
};

export default ParticipantsTable;